import React from 'react';
import './Boulder.css';
import { BoulderCustomization } from './Customization';

interface BoulderProps {
  position: number;
  isMoving: boolean;
  size?: number;
  customization?: BoulderCustomization;
}

const Boulder: React.FC<BoulderProps> = ({ position, isMoving, size = 0, customization = 'default' }) => {
  const getPositionOnPath = (x: number) => {
    const progress = x / 800;
    const y = 350 - (progress * 200);
    const xPos = x + 50;
    return { 
      xPercent: (xPos / 900) * 100, 
      yPercent: (y / 400) * 100 
    };
  };

  const { xPercent, yPercent } = getPositionOnPath(position);
  const scale = 1 + (size * 0.03);

  const getBoulderGradient = () => {
    switch (customization) {
      case 'blue':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#4A90E2', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#357ABD', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#1E5A8A', stopOpacity: 1 }} />
          </>
        );
      case 'green':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#52C41A', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#389E0D', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#237804', stopOpacity: 1 }} />
          </>
        );
      case 'red':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#FF4D4F', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#CF1322', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#A8071A', stopOpacity: 1 }} />
          </>
        );
      case 'yellow':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#FAAD14', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#D48806', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#AD6800', stopOpacity: 1 }} />
          </>
        );
      case 'shiny':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#FFD700', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#FFA500', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#FF8C00', stopOpacity: 1 }} />
          </>
        );
      case 'rainbow':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#FF0000', stopOpacity: 1 }} />
            <stop offset="16.66%" style={{ stopColor: '#FF7F00', stopOpacity: 1 }} />
            <stop offset="33.33%" style={{ stopColor: '#FFFF00', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#00FF00', stopOpacity: 1 }} />
            <stop offset="66.66%" style={{ stopColor: '#0000FF', stopOpacity: 1 }} />
            <stop offset="83.33%" style={{ stopColor: '#4B0082', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#9400D3', stopOpacity: 1 }} />
          </>
        );
      case 'alien':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#00FF00', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#00CC00', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#009900', stopOpacity: 1 }} />
          </>
        );
      case 'pizza':
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#FFA500', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#FF8C00', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#FF7F00', stopOpacity: 1 }} />
          </>
        );
      default:
        return (
          <>
            <stop offset="0%" style={{ stopColor: '#6B5B73', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#4A4A4A', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#2C2C2C', stopOpacity: 1 }} />
          </>
        );
    }
  };

  const getBoulderDetails = () => {
    if (customization === 'pizza') {
      return (
        <>
          {/* Crust edge */}
          <ellipse cx="25" cy="25" rx="22" ry="20" fill="none" stroke="#D2691E" strokeWidth="2" opacity="0.8" />
          
          {/* Pepperoni slices */}
          <ellipse cx="18" cy="18" rx="3.5" ry="2.5" fill="#FF6347" opacity="0.9" />
          <ellipse cx="18" cy="18" rx="2" ry="1.5" fill="#8B0000" opacity="0.6" />
          <ellipse cx="32" cy="20" rx="3" ry="2.5" fill="#FF6347" opacity="0.9" />
          <ellipse cx="32" cy="20" rx="1.8" ry="1.3" fill="#8B0000" opacity="0.6" />
          <ellipse cx="25" cy="28" rx="3.5" ry="2.5" fill="#FF6347" opacity="0.9" />
          <ellipse cx="25" cy="28" rx="2" ry="1.5" fill="#8B0000" opacity="0.6" />
          <ellipse cx="15" cy="30" rx="2.5" ry="2" fill="#FF6347" opacity="0.8" />
          <ellipse cx="35" cy="28" rx="2.5" ry="2" fill="#FF6347" opacity="0.8" />
          
          {/* Mushrooms */}
          <ellipse cx="22" cy="22" rx="2.5" ry="2" fill="#F5DEB3" opacity="0.9" />
          <ellipse cx="22" cy="22" rx="1.8" ry="1.3" fill="#DEB887" opacity="0.7" />
          <ellipse cx="28" cy="25" rx="2" ry="1.8" fill="#F5DEB3" opacity="0.9" />
          <ellipse cx="28" cy="25" rx="1.5" ry="1.2" fill="#DEB887" opacity="0.7" />
          <ellipse cx="30" cy="15" rx="2.2" ry="1.9" fill="#F5DEB3" opacity="0.9" />
          <ellipse cx="30" cy="15" rx="1.6" ry="1.3" fill="#DEB887" opacity="0.7" />
          
          {/* Green peppers */}
          <ellipse cx="20" cy="25" rx="2" ry="3" fill="#32CD32" opacity="0.8" />
          <ellipse cx="20" cy="25" rx="1.2" ry="1.8" fill="#228B22" opacity="0.6" />
          <ellipse cx="28" cy="18" rx="1.8" ry="2.5" fill="#32CD32" opacity="0.8" />
          <ellipse cx="28" cy="18" rx="1" ry="1.5" fill="#228B22" opacity="0.6" />
          
          {/* Olives */}
          <circle cx="17" cy="22" r="1.5" fill="#2F4F2F" opacity="0.9" />
          <circle cx="17" cy="22" r="0.8" fill="#1C1C1C" opacity="0.7" />
          <circle cx="33" cy="24" r="1.5" fill="#2F4F2F" opacity="0.9" />
          <circle cx="33" cy="24" r="0.8" fill="#1C1C1C" opacity="0.7" />
          <circle cx="23" cy="15" r="1.3" fill="#2F4F2F" opacity="0.9" />
          <circle cx="23" cy="15" r="0.7" fill="#1C1C1C" opacity="0.7" />
          
          {/* Cheese bubbles */}
          <circle cx="19" cy="16" r="1.8" fill="#FFD700" opacity="0.7" />
          <circle cx="26" cy="20" r="1.5" fill="#FFD700" opacity="0.7" />
          <circle cx="31" cy="30" r="1.6" fill="#FFD700" opacity="0.7" />
          <circle cx="16" cy="27" r="1.4" fill="#FFD700" opacity="0.7" />
          <circle cx="27" cy="32" r="1.3" fill="#FFD700" opacity="0.7" />
          <circle cx="33" cy="18" r="1.2" fill="#FFD700" opacity="0.7" />
          
          {/* Basil leaves */}
          <ellipse cx="21" cy="14" rx="1.5" ry="2.5" fill="#228B22" opacity="0.8" transform="rotate(-30 21 14)" />
          <ellipse cx="29" cy="12" rx="1.3" ry="2.2" fill="#228B22" opacity="0.8" transform="rotate(45 29 12)" />
          <ellipse cx="14" cy="20" rx="1.2" ry="2" fill="#228B22" opacity="0.8" transform="rotate(60 14 20)" />
        </>
      );
    } else if (customization === 'alien') {
      return (
        <>
          <ellipse cx="20" cy="20" rx="6" ry="5" fill="#00AA00" opacity="0.6" className="boulder-detail detail-1" />
          <ellipse cx="30" cy="28" rx="5" ry="4" fill="#00AA00" opacity="0.5" className="boulder-detail detail-2" />
          <ellipse cx="22" cy="30" rx="4" ry="3" fill="#00CC00" opacity="0.4" className="boulder-detail detail-3" />
          <circle cx="18" cy="18" r="2" fill="#000000" opacity="0.8" />
          <circle cx="32" cy="18" r="2" fill="#000000" opacity="0.8" />
        </>
      );
    } else {
      return (
        <>
          <ellipse cx="20" cy="20" rx="6" ry="5" fill="rgba(0, 0, 0, 0.3)" opacity="0.6" className="boulder-detail detail-1" />
          <ellipse cx="30" cy="28" rx="5" ry="4" fill="rgba(0, 0, 0, 0.3)" opacity="0.5" className="boulder-detail detail-2" />
          <ellipse cx="22" cy="30" rx="4" ry="3" fill="rgba(0, 0, 0, 0.2)" opacity="0.4" className="boulder-detail detail-3" />
        </>
      );
    }
  };

  return (
    <div 
      className={`boulder ${isMoving ? 'moving' : ''}`}
      style={{ 
        left: `${xPercent}%`, 
        top: `${yPercent}%`,
        transform: `translate(-50%, -50%)`,
        '--boulder-scale': scale.toString()
      } as React.CSSProperties & { '--boulder-scale': string }}
    >
      <svg 
        width="50" 
        height="50" 
        viewBox="0 0 50 50" 
        className="boulder-svg"
        style={{ 
          transform: `scale(${scale})`,
          transformOrigin: 'center'
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={`boulderGradient-${customization}`} x1="0%" y1="0%" x2="100%" y2="100%">
            {getBoulderGradient()}
          </linearGradient>
          {customization === 'shiny' && (
            <filter id="shinyGlow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          )}
          <filter id="shadow">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="2" dy="2" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Main boulder shape */}
        <ellipse 
          cx="25" 
          cy="25" 
          rx="20" 
          ry="18" 
          fill={`url(#boulderGradient-${customization})`}
          stroke={customization === 'rainbow' ? '#FFFFFF' : '#1a1a1a'}
          strokeWidth="1.5"
          filter={customization === 'shiny' ? 'url(#shinyGlow)' : 'url(#shadow)'}
          className={`boulder-main ${isMoving ? 'rolling' : ''}`}
        />
        
        {/* Boulder texture/details */}
        {getBoulderDetails()}
        
        {/* Rolling dust particles */}
        {isMoving && (
          <>
            <circle 
              cx="5" 
              cy="35" 
              r="2" 
              fill="#8B7355" 
              opacity="0.6"
              className="dust-particle dust-1"
            />
            <circle 
              cx="8" 
              cy="38" 
              r="1.5" 
              fill="#8B7355" 
              opacity="0.5"
              className="dust-particle dust-2"
            />
            <circle 
              cx="3" 
              cy="40" 
              r="1.5" 
              fill="#8B7355" 
              opacity="0.4"
              className="dust-particle dust-3"
            />
            <circle 
              cx="45" 
              cy="35" 
              r="2" 
              fill="#8B7355" 
              opacity="0.6"
              className="dust-particle dust-4"
            />
            <circle 
              cx="42" 
              cy="38" 
              r="1.5" 
              fill="#8B7355" 
              opacity="0.5"
              className="dust-particle dust-5"
            />
            <circle 
              cx="47" 
              cy="40" 
              r="1.5" 
              fill="#8B7355" 
              opacity="0.4"
              className="dust-particle dust-6"
            />
          </>
        )}
      </svg>
      
      <div 
        className="boulder-shadow"
        style={{
          width: `${30 * scale}px`,
          height: `${10 * scale}px`
        }}
      ></div>
    </div>
  );
};

export default Boulder; 